// client/src/hooks/useAccounts.js - VERSÃO 100% COMPLETA E CORRIGIDA (COM UPLOAD DE BANNER)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';
import { useAuth } from './useAuth';
import { isMatrizAdmin, isFranquiaAdmin } from '../utils/permissions';

const ACCOUNTS_QUERY_KEY = 'accounts';
const ACCOUNT_USERS_QUERY_KEY = 'accountUsers';
const UNIDADES_QUERY_KEY = 'unidades';
const ACCOUNT_I18N_QUERY_KEY = 'accountI18n';

// ============================================================================
// ✅ FUNÇÕES AUXILIARES
// ============================================================================

// ✅ Extrair accountId do usuário
const extractAccountIdFromUser = (user) => {
    if (!user) return null;
    return user.account?._id || user.account || null;
};

// ✅ Validar ObjectId (24 caracteres hexadecimais)
const isValidObjectId = (id) => {
    if (!id || typeof id !== 'string') return false;
    return /^[0-9a-fA-F]{24}$/.test(id);
};

// ✅ Criar objeto de fallback
const createFallbackAccount = (user, tipo = 'fallback') => {
    const accountId = extractAccountIdFromUser(user);
    const validId = isValidObjectId(accountId) ? accountId : '000000000000000000000000';
    
    return {
        _id: validId,
        id: validId,
        name: user?.account?.name || 'Conta',
        razaoSocial: user?.account?.razaoSocial || '',
        tipo: tipo,
        isActive: true,
        isFallback: true,
        user: {
            _id: user?._id || '000000000000000000000000',
            name: user?.name || 'Usuário',
            email: user?.email || '',
            role: user?.role?.name || 'conta'
        }
    };
};

// ✅ Função para processar resposta da API de usuários - CORRIGIDA COM PRESERVAÇÃO DE unidadePrincipal
const processAccountUsersResponse = (data) => {
    console.log('📦 [processAccountUsersResponse] Processando resposta:', data);
    
    if (!data) return [];
    
    let users = [];
    
    // Formato: { success: true, data: [...] }
    if (data.success && Array.isArray(data.data)) {
        users = data.data;
    }
    // Formato: { data: [...] }
    else if (data.data && Array.isArray(data.data)) {
        users = data.data;
    }
    // Formato: [...] (array direto)
    else if (Array.isArray(data)) {
        users = data;
    }
    // Formato: { users: [...] } (caso especial)
    else if (data.users && Array.isArray(data.users)) {
        users = data.users;
    }
    else {
        console.warn('⚠️ [processAccountUsersResponse] Formato não reconhecido:', data);
        return [];
    }
    
    // ✅ GARANTIR QUE CADA USUÁRIO TENHA UM _id E PRESERVAR unidadePrincipal E unidades
    users = users.map(user => {
        // Criar uma cópia do usuário para não modificar o original
        const userWithId = { ...user };
        
        // Se não tiver _id mas tiver id, usar id como _id
        if (!userWithId._id && userWithId.id) {
            userWithId._id = userWithId.id;
        }
        
        // Se não tiver nem _id nem id, tentar usar o _id do vínculo? (improvável)
        if (!userWithId._id && userWithId.userId) {
            userWithId._id = userWithId.userId;
        }
        
        // ✅ PRESERVAR unidadePrincipal (campo crítico)
        if (user.unidadePrincipal) {
            userWithId.unidadePrincipal = user.unidadePrincipal;
        }
        
        // ✅ PRESERVAR unidades (array de subdocumentos)
        if (user.unidades) {
            userWithId.unidades = user.unidades;
        }
        
        // ✅ PRESERVAR metadata se existir
        if (user.metadata) {
            userWithId.metadata = user.metadata;
        }
        
        // ✅ PRESERVAR role se existir
        if (user.role) {
            userWithId.role = user.role;
        }
        
        return userWithId;
    }).filter(user => user._id); // Remover usuários que ainda não têm _id
    
    console.log('✅ [processAccountUsersResponse] Usuários processados:', {
        total: users.length,
        ids: users.map(u => u._id),
        amostra: users[0] ? {
            id: users[0]._id,
            name: users[0].name,
            hasProfissional: !!users[0].profissional,
            hasUnidadePrincipal: !!users[0].unidadePrincipal,
            unidadesCount: users[0].unidades?.length || 0
        } : null
    });
    
    return users;
};

// ============================================================================
// ✅ HOOKS BÁSICOS
// ============================================================================

export const useAccounts = (franquiaId) => {
    return useQuery({
        queryKey: [ACCOUNTS_QUERY_KEY, { franquiaId: franquiaId || 'all' }],
        queryFn: async () => {
            const url = franquiaId ? `/franquias/${franquiaId}/contas` : '/contas';
            const { data } = await api.get(url);
            return data.data || [];
        },
        staleTime: 5 * 60 * 1000,
    });
};

export const useAccountById = (accountId, options = {}) => {
    return useQuery({
        queryKey: [ACCOUNTS_QUERY_KEY, accountId],
        queryFn: async () => {
            if (!accountId) return null;
            const { data } = await api.get(`/contas/${accountId}`);
            return data.data || null;
        },
        enabled: !!accountId && (options.enabled !== undefined ? options.enabled : true),
        staleTime: 5 * 60 * 1000,
    });
};

// ✅✅✅ CORREÇÃO DEFINITIVA: useMyAccountDetails com busca simplificada
export const useMyAccountDetails = () => {
    const { setAlert } = useAlert();
    const { user } = useAuth();

    return useQuery({
        queryKey: [ACCOUNTS_QUERY_KEY, 'myDetails'],
        queryFn: async () => {
            console.log('🔍 [useMyAccountDetails] Iniciando busca de detalhes da conta...');
            
            try {
                // =====================================================
                // 1. ADMINISTRADORES: buscar a primeira conta disponível
                // =====================================================
                // role.name é rótulo de exibição do banco ("Administrador Matriz"),
                // nunca o slug 'matriz_admin'/'franquia_admin'/'super_admin' — essa
                // comparação nunca era verdadeira. isMatrizAdmin/isFranquiaAdmin já
                // cobrem o caso de super_admin (bypass_all_checks) internamente.
                if (isMatrizAdmin(user) || isFranquiaAdmin(user)) {
                    console.log(`ℹ️ [useMyAccountDetails] Usuário é ${user?.role?.name}, buscando primeira conta...`);
                    
                    try {
                        const contasResponse = await api.get('/contas', { params: { limit: 1, status: 'active' } });
                        
                        if (contasResponse.data?.data?.[0]) {
                            const conta = contasResponse.data.data[0];
                            console.log('✅ [useMyAccountDetails] Conta encontrada para admin:', {
                                id: conta._id,
                                name: conta.name
                            });
                            return conta;
                        }
                    } catch (err) {
                        console.warn('⚠️ [useMyAccountDetails] Erro ao buscar conta para admin:', err.message);
                    }
                    
                    // Fallback para admin
                    return createFallbackAccount(user, 'admin');
                }

                // =====================================================
                // 2. USUÁRIOS NORMAIS: tentar rota principal
                // =====================================================
                console.log('🔍 [useMyAccountDetails] Tentando rota principal /contas/my/details...');
                
                try {
                    const { data } = await api.get('/contas/my/details');
                    
                    if (data?.data) {
                        const accountData = data.data;
                        const accountId = accountData._id || accountData.id;
                        
                        // ✅ VERIFICAÇÃO CRÍTICA: ID deve ser diferente do ID do usuário
                        if (accountId && accountId !== user?._id) {
                            console.log('✅ [useMyAccountDetails] Sucesso na rota principal! ID da conta:', accountId);
                            
                            return {
                                _id: accountId,
                                id: accountId,
                                name: accountData.name || accountData.razaoSocial || 'Minha Conta',
                                razaoSocial: accountData.razaoSocial || '',
                                nomeFantasia: accountData.nomeFantasia || '',
                                tipoEmpresa: accountData.tipoEmpresa,
                                status: accountData.status,
                                franquia: accountData.franquia,
                                plano: accountData.plano,
                                unidades: accountData.unidades || [],
                                clientePadrao: accountData.clientePadrao,
                                owner: accountData.owner,
                                cnpj: accountData.cnpj,
                                email: accountData.email,
                                telefone: accountData.telefone,
                                logo: accountData.logo,
                                banner: accountData.banner,
                                pageConfig: accountData.pageConfig,
                                configuracoesGerais: accountData.configuracoesGerais,
                                address: accountData.address,
                                redesSociais: accountData.redesSociais,
                                segmentoAtuacao: accountData.segmentoAtuacao,
                                isActive: accountData.status === 'active'
                            };
                        }
                        
                        console.warn('⚠️ [useMyAccountDetails] Rota principal retornou ID de usuário, não de conta');
                    }
                } catch (error) {
                    console.warn('⚠️ [useMyAccountDetails] Rota principal falhou:', error.message);
                }

                // =====================================================
                // 3. TENTAR VIA ACCOUNT ID DO USUÁRIO
                // =====================================================
                const accountIdFromUser = user?.account?._id || user?.account;
                
                if (accountIdFromUser && isValidObjectId(accountIdFromUser)) {
                    console.log('🔍 [useMyAccountDetails] Tentando buscar conta por ID:', accountIdFromUser);
                    
                    try {
                        const accountResponse = await api.get(`/contas/${accountIdFromUser}`);
                        
                        if (accountResponse.data?.data) {
                            console.log('✅ [useMyAccountDetails] Conta encontrada por ID!');
                            return accountResponse.data.data;
                        }
                    } catch (error) {
                        console.warn('⚠️ [useMyAccountDetails] Erro ao buscar conta por ID:', error.message);
                    }
                }

                // =====================================================
                // 4. FALLBACK: Usar dados do usuário
                // =====================================================
                console.log('⚠️ [useMyAccountDetails] Usando fallback com dados do usuário');
                
                return {
                    _id: accountIdFromUser || '000000000000000000000000',
                    id: accountIdFromUser || '000000000000000000000000',
                    name: user?.account?.name || 'Minha Conta',
                    isActive: true,
                    isFallback: true
                };

            } catch (error) {
                console.error("❌ [useMyAccountDetails] Erro fatal:", error);
                
                // Fallback de emergência
                return {
                    _id: user?.account?._id || user?.account || '000000000000000000000000',
                    id: user?.account?._id || user?.account || '000000000000000000000000',
                    name: user?.account?.name || 'Conta',
                    isActive: true,
                    isFallback: true,
                    isError: true
                };
            }
        },
        staleTime: 2 * 60 * 1000,
        retry: 1,
        onError: (err) => {
            console.error("Erro no useMyAccountDetails:", err);
            setAlert('Erro ao carregar detalhes da conta. Usando dados locais.', 'warning');
        }
    });
};

// ✅ CORREÇÃO: aceita accountId opcional — sem ele, sempre lia
// /contas/my/i18n mesmo quando ContaSettingsPanel é usado com
// isMyAccount=false (matriz/franquia editando a conta de outra pessoa).
// O formulário acabava pré-preenchido com os dados de i18n do PRÓPRIO
// admin (ou vazio/default, já que matriz/franquia normalmente não têm
// AccountUser), nunca com os valores reais da conta sendo editada.
export const useAccountI18n = (accountId) => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: accountId ? [ACCOUNT_I18N_QUERY_KEY, accountId] : [ACCOUNT_I18N_QUERY_KEY],
        queryFn: async () => {
            try {
                const url = accountId ? `/contas/${accountId}/i18n` : '/contas/my/i18n';
                const { data } = await api.get(url);
                return data.data || null;
            } catch (error) {
                console.error("❌ Erro ao buscar i18n:", error);
                return null;
            }
        },
        staleTime: 10 * 60 * 1000,
        retry: 1
    });
};

// ✅ CORREÇÃO CRÍTICA: useAccountUsers com processamento robusto
export const useAccountUsers = (options = {}) => {
    const { setAlert } = useAlert();
    const { user } = useAuth();

    const safeOptions = options || {};

    return useQuery({
        queryKey: [ACCOUNT_USERS_QUERY_KEY, 'my'],
        queryFn: async () => {
            try {
                if (isMatrizAdmin(user)) {
                    console.log('🛑 [useAccountUsers] Matriz Admin bloqueado de usar /my/users');
                    return [];
                }
                
                console.log('🔍 [useAccountUsers] Buscando usuários da conta...');
                const { data } = await api.get('/contas/my/users');
                
                // ✅ Processar a resposta corretamente
                const users = processAccountUsersResponse(data);
                console.log('✅ [useAccountUsers] Usuários carregados:', users.length);
                
                return users;
                
            } catch (error) {
                console.error("❌ Erro ao buscar usuários da minha conta:", error);
                
                // Não mostrar alerta para erro 404
                if (error.response?.status !== 404) {
                    setAlert(error.response?.data?.error || 'Erro ao carregar usuários da conta', 'danger');
                }
                
                return [];
            }
        },
        staleTime: 2 * 60 * 1000,
        enabled: safeOptions.enabled !== undefined ? safeOptions.enabled : true,
        retry: (failureCount, error) => {
            if (error.response?.status === 404) return false;
            return failureCount < 2;
        },
        ...safeOptions
    });
};

export const useAccountUsersById = (accountId, options = {}) => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: [ACCOUNT_USERS_QUERY_KEY, 'by-id', accountId],
        queryFn: async () => {
            try {
                if (!accountId) {
                    throw new Error('ID da conta é obrigatório');
                }
                
                console.log(`🔍 [useAccountUsersById] Buscando usuários da conta ${accountId}...`);
                const { data } = await api.get(`/contas/${accountId}/users`);
                
                const users = processAccountUsersResponse(data);
                console.log(`✅ [useAccountUsersById] Usuários carregados:`, users.length);
                
                return users;
                
            } catch (error) {
                console.error(`❌ Erro ao buscar usuários da conta ${accountId}:`, error);
                if (error.response?.status !== 404) {
                    setAlert(error.response?.data?.error || 'Erro ao carregar usuários da conta', 'danger');
                }
                return [];
            }
        },
        enabled: !!accountId && (options.enabled !== undefined ? options.enabled : true),
        staleTime: 2 * 60 * 1000,
        retry: (failureCount, error) => {
            if (error.response?.status === 404) return false;
            return failureCount < 2;
        },
        ...options
    });
};

export const useAccountUser = (userId, options = {}) => {
    return useQuery({
        queryKey: [ACCOUNT_USERS_QUERY_KEY, userId],
        queryFn: async () => {
            if (!userId) return null;
            
            try {
                const { data } = await api.get(`/contas/my/users/${userId}`);
                return data.data || null;
            } catch (error) {
                try {
                    const { data } = await api.get(`/relationships/account-users/${userId}`);
                    return data.data || null;
                } catch(e) {
                    console.error(`❌ Erro ao buscar usuário ${userId}:`, error);
                    return null;
                }
            }
        },
        enabled: !!userId && (options.enabled !== undefined ? options.enabled : true),
        staleTime: 2 * 60 * 1000,
    });
};

export const useAccountStatistics = (accountId, options = {}) => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: [ACCOUNTS_QUERY_KEY, 'statistics', accountId],
        queryFn: async () => {
            try {
                const url = accountId ? `/contas/${accountId}/statistics` : '/contas/my/statistics';
                const { data } = await api.get(url);
                return data.data || {};
            } catch (error) {
                console.error("❌ Erro ao buscar estatísticas:", error);
                setAlert(error.response?.data?.error || 'Erro ao carregar estatísticas', 'danger');
                throw error;
            }
        },
        enabled: options.enabled !== false,
        staleTime: 5 * 60 * 1000,
        ...options
    });
};

export const useAccountPageConfig = (accountId, options = {}) => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: [ACCOUNTS_QUERY_KEY, 'page-config', accountId],
        queryFn: async () => {
            try {
                const url = accountId ? `/contas/${accountId}/page-config` : '/contas/my/page-config';
                const { data } = await api.get(url);
                return data.data || {};
            } catch (error) {
                console.error("❌ Erro ao buscar configuração da página:", error);
                setAlert(error.response?.data?.error || 'Erro ao carregar configuração da página', 'danger');
                throw error;
            }
        },
        enabled: options.enabled !== false,
        staleTime: 10 * 60 * 1000,
        ...options
    });
};

// ============================================================================
// ✅ HOOK ADAPTIVO PARA ACCOUNT USERS
// ============================================================================

export const useAccountUsersAdaptive = (accountId = null, options = {}) => {
    const { setAlert } = useAlert();
    const { user } = useAuth();

    // ✅ CORREÇÃO: usa os helpers centralizados (já importados no topo do
    // arquivo) em vez de recomputar via hasPermission()+role.name — mesma
    // classe de drift do useUserType.js, ver comentário lá.
    const isCurrentUserMatrizAdmin = isMatrizAdmin(user);
    const isCurrentUserFranquiaAdmin = isFranquiaAdmin(user);

    const effectiveAccountId = accountId || user?.account?._id || user?.account;

    return useQuery({
        queryKey: [ACCOUNT_USERS_QUERY_KEY, 'adaptive', effectiveAccountId],
        queryFn: async () => {
            if (!effectiveAccountId) {
                console.warn('⚠️ [useAccountUsersAdaptive] Nenhum accountId disponível');
                return [];
            }

            console.log(`🔍 [useAccountUsersAdaptive] Buscando usuários para conta: ${effectiveAccountId}`);

            try {
                // 1. Tentar rota principal via contas
                if (!isCurrentUserMatrizAdmin) {
                    try {
                        const { data } = await api.get(`/contas/${effectiveAccountId}/users`);
                        const users = processAccountUsersResponse(data);
                        console.log(`✅ [useAccountUsersAdaptive] Sucesso via /contas/${effectiveAccountId}/users:`, users.length);
                        return users;
                    } catch (error) {
                        console.warn('⚠️ [useAccountUsersAdaptive] Falha na rota principal:', error.message);
                    }
                }

                // 2. Tentar via relationships
                try {
                    const { data } = await api.get(`/relationships/accounts/${effectiveAccountId}/users`);
                    const users = processAccountUsersResponse(data);
                    console.log(`✅ [useAccountUsersAdaptive] Sucesso via relationships:`, users.length);
                    return users;
                } catch (error) {
                    console.warn('⚠️ [useAccountUsersAdaptive] Falha na rota relationships:', error.message);
                }

                // 3. Tentar via account-users direto
                try {
                    const { data } = await api.get('/account-users', {
                        params: { account: effectiveAccountId }
                    });
                    const users = processAccountUsersResponse(data);
                    console.log(`✅ [useAccountUsersAdaptive] Sucesso via account-users:`, users.length);
                    return users;
                } catch (error) {
                    console.warn('⚠️ [useAccountUsersAdaptive] Falha na rota account-users:', error.message);
                }

                // 4. Se tudo falhar, retornar array vazio
                console.warn('⚠️ [useAccountUsersAdaptive] Todas as rotas falharam');
                return [];

            } catch (error) {
                console.error(`❌ [useAccountUsersAdaptive] Erro final:`, error);
                return [];
            }
        },
        enabled: !!effectiveAccountId && (options.enabled !== false),
        staleTime: 2 * 60 * 1000,
        retry: (failureCount, error) => {
            if (error.response?.status === 404) return false;
            return failureCount < 2;
        },
        ...options
    });
};

export const useAccountUsersUniversal = (accountId = null, options = {}) => {
    return useAccountUsersAdaptive(accountId, options);
};

export const useSafeAccountUsers = (accountId, options = {}) => {
    return useAccountUsersAdaptive(accountId, options);
};

// ============================================================================
// ✅ HOOKS DE UTILIDADE
// ============================================================================

export const useUnidadesProfissional = (profissionalId, accountId = null) => {
    return useQuery({
        queryKey: ['unidades-profissional', profissionalId, accountId],
        queryFn: async () => {
            if (!profissionalId) {
                console.warn('❌ ProfissionalId não fornecido para useUnidadesProfissional');
                return [];
            }

            try {
                const { data } = await api.get(`/relationship/users/${profissionalId}/unidades`, {
                    params: { accountId }
                });
                
                const unidadesVinculadas = data.data || [];
                
                console.log(`📍 [useUnidadesProfissional] Encontradas ${unidadesVinculadas.length} unidades vinculadas`);
                
                return unidadesVinculadas.map(vinculo => ({
                    ...vinculo.unidade,
                    relationshipId: vinculo._id,
                    role: vinculo.role,
                    status: vinculo.status
                }));
            } catch (error) {
                console.error(`❌ Erro ao buscar unidades do profissional ${profissionalId}:`, error);
                return [];
            }
        },
        enabled: !!profissionalId,
        staleTime: 10 * 60 * 1000,
        retry: (failureCount, error) => {
            if (error.response?.status === 404) return false;
            return failureCount < 2;
        },
    });
};

export const useUnidadesConta = () => {
    const { setAlert } = useAlert();

    return useQuery({
        queryKey: [UNIDADES_QUERY_KEY, 'minhaConta'],
        queryFn: async () => {
            try {
                const { data } = await api.get('/unidades', {
                    params: { limit: 100 }
                });
                return data.data || [];
            } catch (error) {
                console.error("❌ Erro ao buscar unidades da conta:", error);
                setAlert('Erro ao carregar unidades', 'danger');
                return [];
            }
        },
        staleTime: 5 * 60 * 1000,
    });
};

export const useMyAccountData = () => {
    const { data: accountDetails, isLoading: isLoadingAccount, error: accountError } = useMyAccountDetails();
    const { data: accountUsers, isLoading: isLoadingUsers, error: usersError } = useAccountUsers();
    const { data: unidades, isLoading: isLoadingUnidades, error: unidadesError } = useUnidadesConta();
    const { data: i18nConfig, isLoading: isLoadingI18n, error: i18nError } = useAccountI18n();

    return {
        accountDetails,
        accountUsers,
        unidades,
        i18nConfig,
        
        isLoading: isLoadingAccount || isLoadingUsers || isLoadingUnidades || isLoadingI18n,
        isLoadingAccount,
        isLoadingUsers,
        isLoadingUnidades,
        isLoadingI18n,
        
        error: accountError || usersError || unidadesError || i18nError,
        accountError,
        usersError,
        unidadesError,
        i18nError,
        
        hasData: !!(accountDetails && accountUsers && unidades),
    };
};

// ✅ ACHADO CRÍTICO: hook existia mas nunca era usado em lugar nenhum do
// app (grep confirmado) — e além disso chamava useAccountI18n() sem
// accountId, que só funciona pra escopo 'conta' (bate em /contas/my/i18n).
// Pra matriz/franquia vendo a conta SELECIONADA (mesmo mecanismo já usado
// em toda chamada de API — accountId do useAuth(), que resolve pra
// getValidAccountId() por baixo: conta própria pro escopo conta, conta
// selecionada em localStorage pra matriz), passar accountId explícito faz
// bater em /contas/:id/i18n com a conta certa. Sem accountId nenhum
// resolvido (matriz sem conta selecionada, telas agregadas de
// plataforma), cai no default BRL/pt-BR — correto, já que não há UMA
// conta cujo idioma/moeda faça sentido aplicar.
export const useAccountI18nConfig = () => {
    const { accountId } = useAuth();
    const { data: i18nConfig, isLoading, error } = useAccountI18n(accountId || undefined);

    const defaultConfig = {
        locale: 'pt-BR',
        dateFormat: 'DD/MM/YYYY',
        timeFormat: '24h',
        currency: 'BRL',
        firstDayOfWeek: 0,
        timezone: 'America/Sao_Paulo'
    };

    const resolved = i18nConfig || defaultConfig;

    return {
        i18nConfig: resolved,
        currency: resolved.currency,
        locale: resolved.locale,
        dateFormat: resolved.dateFormat,
        timeFormat: resolved.timeFormat,
        firstDayOfWeek: resolved.firstDayOfWeek,
        isLoading,
        error,
        isDefault: !i18nConfig
    };
};

// Alias mais descritivo pro mesmo hook — "tenant" porque resolve pro
// escopo certo (conta própria ou conta selecionada por matriz/franquia),
// não necessariamente "a conta do usuário logado".
export const useTenantI18n = useAccountI18nConfig;

export const useUserHasAccountAccess = (accountId) => {
    const { data: accountUsers } = useAccountUsers();
    
    const hasAccess = accountUsers?.some(accountUser => 
        accountUser.account?._id === accountId || accountUser.account === accountId
    );

    return {
        hasAccess,
        isLoading: !accountUsers,
        accountUsers
    };
};

// ============================================================================
// ✅ HOOKS DE MUTAÇÃO
// ============================================================================

export const useMutateAccounts = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateAllAccountCaches = (accountId) => {
        queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [UNIDADES_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [ACCOUNT_I18N_QUERY_KEY] });
        
        if (accountId) {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, accountId] });
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY, 'by-id', accountId] });
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY, 'adaptive', accountId] });
        }
        queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, 'myDetails'] });
    };

    const createAccount = useMutation({
        mutationFn: async ({ franquiaId, contaData }) => {
            const url = franquiaId ? `/franquias/${franquiaId}/contas` : '/contas';
            const response = await api.post(url, contaData);
            return response.data;
        },
        onSuccess: (data) => {
            invalidateAllAccountCaches();
            setAlert(data.message || 'Conta criada com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao criar conta:", err);
            setAlert(err.response?.data?.error || 'Erro ao criar conta', 'danger');
        },
    });

    const updateAccount = useMutation({
        mutationFn: async ({ accountId, accountData }) => {
            const response = await api.put(`/contas/${accountId}`, accountData);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateAllAccountCaches(variables.accountId);
            setAlert(data.message || 'Conta atualizada com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao atualizar conta:", err);
            setAlert(err.response?.data?.error || 'Erro ao atualizar a conta', 'danger');
        },
    });
    
    const updateMyAccount = useMutation({
        mutationFn: async (formData) => {
            const response = await api.put('/contas/my/details', formData);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, 'myDetails'] });
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_I18N_QUERY_KEY] });
            setAlert(data.message || 'Detalhes da conta atualizados!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao atualizar detalhes da conta:", err);
            setAlert(err.response?.data?.error || 'Erro ao atualizar detalhes', 'danger');
        },
    });

    const updateAccountI18n = useMutation({
        mutationFn: async ({ accountId, i18nData }) => {
            const url = accountId ? `/contas/${accountId}/i18n` : '/contas/my/i18n';
            const response = await api.put(url, i18nData);
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_I18N_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, 'myDetails'] });
            if (variables.accountId) {
                queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, variables.accountId] });
            }
            setAlert(data.message || 'Configurações de idioma atualizadas com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao atualizar configurações de idioma:", err);
            setAlert(err.response?.data?.error || 'Erro ao atualizar configurações de idioma', 'danger');
        },
    });
    
    const uploadAccountLogo = useMutation({
        mutationFn: async ({ file, isMyAccount = true, accountId = null }) => {
            const formData = new FormData();
            formData.append('file', file);
            
            let url;
            if (isMyAccount) {
                url = '/contas/my/logo';
            } else {
                if (!accountId) {
                    throw new Error('accountId é obrigatório para upload em outras contas');
                }
                url = `/contas/${accountId}/logo`;
            }
            
            console.log('🔄 [uploadAccountLogo] Fazendo upload para:', url);
            
            const response = await api.put(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, 'myDetails'] });
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
            
            if (!variables.isMyAccount && variables.accountId) {
                queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, variables.accountId] });
            }
        },
        onError: (err) => {
            console.error("❌ Erro no upload do logo:", err);
            setAlert(err.response?.data?.error || 'Erro no upload do logo', 'danger');
        },
    });

    // ✅ NOVA MUTATION PARA UPLOAD DE BANNER
    const uploadAccountBanner = useMutation({
        mutationFn: async ({ file, isMyAccount = true, accountId = null }) => {
            const formData = new FormData();
            formData.append('file', file);
            
            let url;
            if (isMyAccount) {
                url = '/contas/my/banner';
            } else {
                if (!accountId) {
                    throw new Error('accountId é obrigatório para upload em outras contas');
                }
                url = `/contas/${accountId}/banner`;
            }
            
            console.log('🔄 [uploadAccountBanner] Fazendo upload para:', url);
            
            const response = await api.put(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, 'myDetails'] });
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
            
            if (!variables.isMyAccount && variables.accountId) {
                queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, variables.accountId] });
            }
        },
        onError: (err) => {
            console.error("❌ Erro no upload do banner:", err);
            setAlert(err.response?.data?.error || 'Erro no upload do banner', 'danger');
        },
    });

    const updatePageConfig = useMutation({
        mutationFn: async ({ accountId, configData }) => {
            const url = accountId ? `/contas/${accountId}/page-config` : '/contas/my/page-config';
            const response = await api.put(url, configData);
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, 'page-config', variables.accountId] });
            setAlert(data.message || 'Configuração da página atualizada com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao atualizar configuração da página:", err);
            setAlert(err.response?.data?.error || 'Erro ao atualizar configuração da página', 'danger');
        },
    });

    const togglePublicPage = useMutation({
        mutationFn: async (accountId) => {
            const url = accountId ? `/contas/${accountId}/page-config/toggle` : '/contas/my/page-config/toggle';
            const response = await api.patch(url);
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY, 'page-config', variables] });
            setAlert(data.message || 'Status da página pública alterado com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao alternar página pública:", err);
            setAlert(err.response?.data?.error || 'Erro ao alternar página pública', 'danger');
        },
    });

    return { 
        createAccount, 
        updateAccount, 
        updateMyAccount, 
        updateAccountI18n,
        uploadAccountLogo,
        uploadAccountBanner,
        updatePageConfig,
        togglePublicPage
    };
};

export const useMutateAccountUsers = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateUsersCache = (accountId = null) => {
        queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: ['unidades-profissional'] });
        queryClient.invalidateQueries({ queryKey: ['userUnidades'] });
        
        if (accountId) {
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY, 'by-id', accountId] });
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY, 'adaptive', accountId] });
        }
    };

    const addAccountUser = useMutation({
        mutationFn: async (userData) => {
            const { accountId, ...data } = userData;
            const url = accountId ? `/contas/${accountId}/users` : '/contas/my/users';
            const response = await api.post(url, data);
            return response.data;
        },
        onSuccess: (data) => {
            invalidateUsersCache(data.targetAccountId);
            setAlert(data.message || 'Usuário adicionado com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao adicionar usuário:", err);
            setAlert(err.response?.data?.error || 'Erro ao adicionar usuário', 'danger');
        },
    });

    const updateAccountUser = useMutation({
        mutationFn: async ({ userId, userData, accountId }) => {
            const url = accountId 
                ? `/contas/${accountId}/users/${userId}` 
                : `/contas/my/users/${userId}`;
                
            const response = await api.put(url, userData);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateUsersCache(variables.accountId);
            setAlert(data.message || 'Usuário atualizado com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao atualizar usuário:", err);
            setAlert(err.response?.data?.error || 'Erro ao atualizar usuário', 'danger');
        },
    });

    const toggleUserActiveStatus = useMutation({
        mutationFn: async ({ userId, isActive, accountId }) => {
            const url = accountId 
                ? `/contas/${accountId}/users/${userId}` 
                : `/contas/my/users/${userId}`;
                
            const response = await api.put(url, { isActive });
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateUsersCache(variables.accountId);
            setAlert(data.message || `Status do usuário alterado para ${data.data?.isActive ? 'Ativo' : 'Inativo'}.`, 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao alterar status do usuário:", err);
            setAlert(err.response?.data?.error || 'Erro ao alterar status do usuário', 'danger');
        },
    });

    const resetAccountUserPassword = useMutation({
        mutationFn: async ({ userId, accountId }) => {
            const url = accountId 
                ? `/contas/${accountId}/users/${userId}/reset-password`
                : `/contas/my/users/${userId}/reset-password`;
                
            const response = await api.post(url);
            return response.data;
        },
        onSuccess: (data) => {
            setAlert(data.message || 'Senha resetada com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao resetar senha:", err);
            setAlert(err.response?.data?.error || 'Erro ao resetar senha', 'danger');
        },
    });

    const deleteAccountUser = useMutation({
        mutationFn: async ({ userId, accountId }) => {
            const url = accountId 
                ? `/contas/${accountId}/users/${userId}`
                : `/contas/my/users/${userId}`;
                
            const response = await api.delete(url);
            return response.data;
        },
        onSuccess: (data, variables) => {
            invalidateUsersCache(variables.accountId);
            setAlert(data.message || 'Usuário removido com sucesso!', 'success');
        },
        onError: (err) => {
            console.error("❌ Erro ao deletar usuário:", err);
            setAlert(err.response?.data?.error || 'Erro ao deletar usuário', 'danger');
        },
    });

    return { 
        addAccountUser, 
        updateAccountUser, 
        toggleUserActiveStatus, 
        resetAccountUserPassword, 
        deleteAccountUser
    };
};

// ============================================================================
// ✅ EXPORTS PADRÃO
// ============================================================================

export default {
    useAccounts,
    useAccountById,
    useMyAccountDetails,
    useAccountI18n,
    useAccountUsers,
    useAccountUsersById,
    useAccountUser,
    useAccountStatistics,
    useAccountPageConfig,
    useAccountUsersAdaptive,
    useAccountUsersUniversal,
    useSafeAccountUsers,
    useUnidadesProfissional,
    useUnidadesConta,
    useMyAccountData,
    useAccountI18nConfig,
    useTenantI18n,
    useUserHasAccountAccess,
    useMutateAccounts,
    useMutateAccountUsers
};